// ─────────────────────────────────────────────────────────────
// ZIFEK — Son d'alerte « nouvelle commande »
// Le marchand choisit le son joué quand une commande arrive :
//   • default → carillon synthétisé (aucun fichier requis)
//   • custom  → un fichier audio qu'il a importé
//   • none    → silencieux
// La préférence est un réglage d'interface stocké localement (localStorage).
// ─────────────────────────────────────────────────────────────

export type OrderSoundMode = 'default' | 'custom' | 'none';

export interface OrderSoundPreference {
  mode: OrderSoundMode;
  url: string | null;
}

const STORAGE_KEY = 'zifek_order_sound';

/** Lit la préférence sonore du marchand (valeur par défaut : carillon). */
export function getOrderSoundPreference(): OrderSoundPreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mode: 'default', url: null };
    const parsed = JSON.parse(raw) as Partial<OrderSoundPreference>;
    const mode: OrderSoundMode =
      parsed.mode === 'custom' || parsed.mode === 'none' ? parsed.mode : 'default';
    const url = typeof parsed.url === 'string' && parsed.url ? parsed.url : null;
    // Un son personnalisé sans fichier retombe sur le carillon par défaut.
    if (mode === 'custom' && !url) return { mode: 'default', url: null };
    return { mode, url };
  } catch {
    return { mode: 'default', url: null };
  }
}

/** Enregistre la préférence sonore du marchand. */
export function setOrderSoundPreference(pref: OrderSoundPreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pref));
  } catch {
    /* best-effort : le réglage reste dans l'état React courant */
  }
}

/** Carillon léger synthétisé (aucun fichier audio requis). */
export function playDefaultChime(): void {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    [880, 1174.66].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = now + i * 0.16;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.22, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.32);
    });
    window.setTimeout(() => {
      void ctx.close().catch(() => {});
    }, 800);
  } catch {
    /* audio best-effort */
  }
}

/** Joue un fichier audio (URL) — utilisé pour l'aperçu et l'alerte personnalisée. */
export function playSoundUrl(url: string): void {
  try {
    const audio = new Audio(url);
    audio.volume = 1;
    void audio.play().catch(() => {
      /* lecture bloquée tant qu'il n'y a pas d'interaction : best-effort */
    });
  } catch {
    /* best-effort */
  }
}

/** Joue le son d'alerte selon la préférence enregistrée. */
export function playOrderSound(): void {
  const pref = getOrderSoundPreference();
  if (pref.mode === 'none') return;
  if (pref.mode === 'custom' && pref.url) {
    playSoundUrl(pref.url);
    return;
  }
  playDefaultChime();
}